/**
 * NEOPINE: 无缝循环滚动 —— 自定义元素 <np-scroll-seamless>
 *
 * 对标 xkah.us 的 <scroll-seamless>，机制一致：
 *   1) 量出「原始列表」总高（末项 offsetTop − 首项 offsetTop + 末项高度）与项间距；
 *   2) 把整份列表克隆 ceil(容器尺寸 ÷ 原列表尺寸) 份追加到尾部
 *      （克隆项加 aria-hidden="true"，读屏不会把评价重复念一遍）；
 *   3) 写三个 CSS 变量：--from / --end（位移区间）、--scroll-speed（时长）；
 *   4) 打上 data-init，触发 CSS 的 linear infinite 动画。
 * 位移区间刚好等于「原列表高 + 一个间距」= 一个完整周期，所以循环处接得上、不跳帧。
 *
 * 时长 =（原列表高 + 间距）÷ 每秒像素 ⇒ 评价越多一圈越久，但**视觉线速度恒定**。
 *
 * 用法：
 *   <np-scroll-seamless data-vertical data-direction="up|down" data-speed="40"
 *                       style="--np-fade: 80px">
 *     <div class="np-scroll-container">
 *       <ul class="np-scroll-list">
 *         <li class="np-scroll-item">…</li>
 *       </ul>
 *     </div>
 *   </np-scroll-seamless>
 *
 * 行为说明（老师 09-20 明确「严格照搬对标」，故以下三处同样不做）：
 *   - 不做 prefers-reduced-motion 降级（补法见文件末尾注释）；
 *   - 不做触屏暂停；
 *   - 滚出视口后不暂停动画（只对「初始化」做了懒触发）。
 *
 * 与对标的内部差异（不影响观感，属健壮性补齐）：
 *   - 初始化前等 document.fonts.ready，避免字体定稿后行高变化导致接缝错位；
 *   - 尺寸变化（窗口缩放 / 容器宽度变化）时清掉克隆重新初始化，避免布局错位；
 *   - 克隆项里的图片改为 eager：克隆节点在入 DOM 前就把 loading 改掉，
 *     否则 transform 位移进视野的懒加载图会短暂空白。
 */

(() => {
  if (customElements.get('np-scroll-seamless')) return;

  /** 克隆项标记 */
  const CLONE_CLASS = 'np-scroll-item-clone';

  class NpScrollSeamless extends HTMLElement {
    connectedCallback() {
      if (this.hasConnected) return;
      this.hasConnected = true;

      this.list = this.querySelector('.np-scroll-list');
      this.isVertical = this.hasAttribute('data-vertical');
      this.direction =
        this.getAttribute('data-direction') || (this.isVertical ? 'up' : 'left');
      this.speed = parseInt(this.getAttribute('data-speed'), 10) || 40;
      this.mode = this.getAttribute('data-mode') || 'clone';

      if (!this.list) return;
      this.observeInView();
    }

    disconnectedCallback() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      if (this.ro) this.ro.disconnect();
      if (this.windowHandler) {
        window.removeEventListener('resize', this.windowHandler);
      }
    }

    /** 进入视口才初始化；再等字体定稿，保证量到的高度是最终值 */
    observeInView() {
      const run = () => {
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.init()).catch(() => this.init());
        } else {
          this.init();
        }
        this.observeResize();
      };

      if (!('IntersectionObserver' in window)) {
        run();
        return;
      }

      const io = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          run();
        },
        { root: null, rootMargin: '100px 0px 100px 0px' }
      );
      io.observe(this);
    }

    /** 窗口缩放 / 容器尺寸变化 → 去抖后重建 */
    observeResize() {
      this.windowHandler = () => this.scheduleRebuild();
      window.addEventListener('resize', this.windowHandler);

      if ('ResizeObserver' in window) {
        let last = this.getBoundingClientRect().width;
        this.ro = new ResizeObserver((entries) => {
          const w = entries[0].contentRect.width;
          if (Math.abs(w - last) < 1) return;
          last = w;
          this.scheduleRebuild();
        });
        this.ro.observe(this);
      }
    }

    scheduleRebuild() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.rebuild(), 300);
    }

    /** 清掉克隆与状态后重新初始化（对标只在 design mode 做，我们常态也做） */
    rebuild() {
      this.removeAttribute('data-init');
      this.querySelectorAll('.' + CLONE_CLASS).forEach((el) => el.remove());
      this.init();
    }

    init() {
      this.removeClones();

      this.getAllItems();
      if (!this.items.length) return;

      this.getOriginalTotalSize();
      if (!(this.originalSize > 0)) return;

      if (this.mode === 'clone') this.cloneItems();

      this.getAllItems();
      this.getGap();

      // 位移区间恒为「一个周期」= 原列表高 + 一个间距。
      //   正向（up / left）：从 0 平移到 -distance（内容往左上走）
      //   反向（down / right）：从 -distance 平移回 0（内容往右下走）
      // 两个端点相差正好一个周期，而列表被克隆后是 periodic(distance) 的，
      // 所以循环点上的画面完全重合 —— 不会有断层。
      //
      // 🔴 不要再用 `clientHeight − scrollHeight` 去算「反向」的起点：
      //    自定义元素里唯一的子节点是 .np-scroll-container（height:100% +
      //    overflow:hidden），内容全被这个容器裁掉了，因此**元素自身**的
      //    scrollHeight 恒等于 clientHeight ⇒ offset 恒为 0 ⇒ down 变成
      //    「从 0 平移到 +distance」，整列滚出可视区、周期性露出空白。
      //    （老师 09-20 报的「右边跑马有断层」就是这个。）
      const distance = this.originalSize + this.gap;
      const forward = this.direction === 'up' || this.direction === 'left';
      this.style.setProperty('--from', forward ? '0px' : `-${distance}px`);
      this.style.setProperty('--end', forward ? `-${distance}px` : '0px');

      this.style.setProperty('--scroll-speed', `${distance / this.speed}s`);
      this.setAttribute('data-init', 'true');
    }

    removeClones() {
      this.querySelectorAll('.' + CLONE_CLASS).forEach((el) => el.remove());
    }

    getAllItems() {
      this.items = Array.from(this.querySelectorAll('.np-scroll-item'));
    }

    /** 原始列表总高：首项对首项、末项算到自身底部 */
    getOriginalTotalSize() {
      if (this.isVertical) {
        const first = this.items[0];
        const last = this.items[this.items.length - 1];
        this.originalSize = last.offsetTop - first.offsetTop + last.offsetHeight;
      } else {
        const first = this.items[0];
        const last = this.items[this.items.length - 1];
        this.originalSize = last.offsetLeft - first.offsetLeft + last.offsetWidth;
      }
    }

    /** 项间距：两两相邻的实际留白（不依赖 CSS 里写的是 gap 还是 margin） */
    getGap() {
      if (this.items.length < 2) {
        this.gap = this.isVertical ? 16 : 16;
        return;
      }
      const [a, b] = this.items;
      this.gap = this.isVertical
        ? b.offsetTop - a.offsetTop - a.offsetHeight
        : b.offsetLeft - a.offsetLeft - a.offsetWidth;
    }

    /** 克隆到「填满容器」为止；克隆项对读屏隐藏，图片改 eager */
    cloneItems() {
      const containerSize = this.isVertical ? this.clientHeight : this.clientWidth;
      if (containerSize <= 0) return;

      const times = Math.max(1, Math.ceil(containerSize / this.originalSize));
      const fragment = document.createDocumentFragment();

      for (let i = 0; i < times; i++) {
        this.items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.classList.add(CLONE_CLASS);
          clone.setAttribute('aria-hidden', 'true');
          // 克隆节点还没进 DOM，此刻改 loading 才来得及（否则被 transform 带进
          // 视野的懒加载图会短暂空白）
          clone.querySelectorAll('img[loading="lazy"]').forEach((img) => {
            img.setAttribute('loading', 'eager');
          });
          fragment.appendChild(clone);
        });
      }

      this.list.appendChild(fragment);
    }
  }

  customElements.define('np-scroll-seamless', NpScrollSeamless);
})();

/**
 * 日后若要补「系统减少动态效果」降级，在 assets/neopine-custom.css 里加：
 *
 *   @media (prefers-reduced-motion: reduce) {
 *     .np-review np-scroll-seamless[data-init] .np-scroll-list { animation: none; }
 *     .np-review np-scroll-seamless .np-scroll-container { overflow-y: auto; }
 *   }
 *
 * 本轮按老师要求严格照搬对标，故未启用。
 */
